import { z } from "zod";
import { defineEndpoint } from "../endpoint.js";
/**
 * Device heartbeat and sync health (docs/specs/pos-sync.md "Heartbeat and device status", PRD §10.5, B-51). The POS
 * reports every minute; Admin shows each device's last contact and pending count, and managers are told when a
 * device is online but has had bills waiting for over 15 minutes.
 */
/** `1.4.0`, `1.5.0-beta.2`: the app's version as built. */
export const appVersionSchema = z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}(-[0-9A-Za-z.-]{1,40})?$/);
/** A device counts as online while its last heartbeat is at most this old. */
export const DEVICE_ONLINE_WINDOW_MS = 5 * 60_000;
/** Pending commands older than this on an online device raise the alert. */
export const SYNC_STALL_AFTER_MS = 15 * 60_000;
/** `POST /v1/pos/heartbeat`, every 60 s. Times are device times; `clockOffsetMs` = server − device. */
export const heartbeatRequestSchema = z
    .object({
    appVersion: appVersionSchema,
    /** Outbox commands not yet acknowledged. */
    pendingCount: z.int().min(0),
    oldestPendingAt: z.iso.datetime().nullable(),
    /** Every command up to this `deviceSeq` has a result on the device. */
    ackedThroughSeq: z.int().min(0),
    feedCursor: z.int().min(0).nullable(),
    clockOffsetMs: z.int().nullable(),
    openShiftId: z.uuid().nullable(),
    dbSizeBytes: z.int().min(0).nullable(),
})
    .strict();
export const heartbeatResponseSchema = z.object({
    serverTime: z.iso.datetime(),
    /** Below this the app must update before it may sell (B-53); null = no minimum. */
    minVersion: appVersionSchema.nullable(),
    /** Below this the server refuses pushes with `APP_UPGRADE_REQUIRED` (B-53); null = no minimum. */
    minPushVersion: appVersionSchema.nullable(),
    /** Always false here: a revoked device is answered `DEVICE_REVOKED` instead. */
    revoked: z.boolean(),
    /**
     * SA-11: the business's account (B-62). A suspended account keeps selling and syncing; the till only shows a notice.
     * Absent from older servers.
     */
    accountStatus: z.enum(["trial", "active", "restricted", "suspended", "closed"]).optional(),
});
/** What the server knows of a device's sync (Admin's devices screen). */
export const deviceSyncHealthSchema = z.object({
    lastSeenAt: z.iso.datetime().nullable(),
    appVersion: z.string().nullable(),
    /** As last reported by the device. */
    pendingCount: z.int().min(0),
    /** Device time corrected by its clock offset (server time). */
    oldestPendingAt: z.iso.datetime().nullable(),
    ackedThroughSeq: z.int().min(0),
    /** The last `deviceSeq` the server processed. */
    serverLastSeq: z.int().min(0),
    lastPushAt: z.iso.datetime().nullable(),
    clockOffsetMs: z.int().nullable(),
    openShiftId: z.uuid().nullable(),
    /** A heartbeat within the last 5 minutes. */
    online: z.boolean(),
    /** Online with commands waiting for over 15 minutes, or a seq gap open for as long. */
    stalled: z.boolean(),
    /** Set while the server waits for a missing `deviceSeq`. */
    gapExpectedSeq: z.int().min(1).nullable(),
});
/** Data of `pos.device.sync_stalled`. */
export const deviceSyncStalledEventSchema = z.object({
    deviceId: z.uuid(),
    pendingCount: z.int().min(0),
    oldestPendingAt: z.iso.datetime().nullable(),
    gapExpectedSeq: z.int().min(1).nullable(),
});
export const sendHeartbeat = defineEndpoint({
    tag: "pos-sync",
    name: "sendHeartbeat",
    method: "POST",
    path: "/v1/pos/heartbeat",
    access: { device: true },
    summary: "The device's sync state every minute; answers the server time and the minimum app versions",
    body: heartbeatRequestSchema,
    status: 200,
    response: heartbeatResponseSchema,
});
//# sourceMappingURL=health.js.map